import { cloneDeep } from 'lodash-es';
import type { OptionsItemType } from '@home/components/NewRegionSelect/type';
import { getCascaderMetricsOptionsAndValue } from '@/pages/intelligence/pages/TopCharts/PCGames/MetricsTable/utils';
import type { PCConsolePageId } from '@/pages/intelligence/pages/TopCharts/PCGames/_config/type';
import { validateOrderFields } from '@intelligence/v3/utils/topcharts';
import { isEmpty } from '@intelligence/v3/utils/common';
import * as filterKeyMap from '@intelligence/components/Filter/FilterBase/_config';

export interface FilterDisabledMetricsResult {
  /** 过滤后的可用指标值列表（category###metric 格式） */
  availableMetricValues: string[];
  /** 过滤后的 source_metrics */
  sourceMetrics: Record<string, string[]>;
  /** 校验后的排序指标数据源 */
  orderSource: string;
  /** 校验后的排序指标 */
  orderMetric: string;
}

export const WISHLISTS_SOURCE_METRIC = 'gamalytic__wishlists_total';
export const WISHLISTS_METRIC_SELECTOR_VALUE = `gamalytic###${WISHLISTS_SOURCE_METRIC}`;

const hasWishlistsRangeValue = (value: { min?: string | number; max?: string | number } = {}): boolean => {
  return !isEmpty(value?.min) || !isEmpty(value?.max);
};

const hasWishlistsMetricFilter = (metricFilter: Record<string, any[]> = {}): boolean => {
  return Boolean(
    metricFilter?.gamalytic?.some((item: any) => item?.metric === WISHLISTS_SOURCE_METRIC),
  );
};

const ensureWishlistsMetricSelection = (
  metricSelectorValues: string[] = [],
  shouldKeepWishlistsMetric = false,
): string[] => {
  if (!shouldKeepWishlistsMetric || metricSelectorValues.includes(WISHLISTS_METRIC_SELECTOR_VALUE)) {
    return metricSelectorValues;
  }

  return [...metricSelectorValues, WISHLISTS_METRIC_SELECTOR_VALUE];
};

const getWishlistsPreservedMetricValues = (shouldKeepWishlistsMetric = false): string[] => {
  if (!shouldKeepWishlistsMetric) {
    return [];
  }

  return [WISHLISTS_METRIC_SELECTOR_VALUE];
};

export const isWishlistsRangeActive = (filterValue: any = {}): boolean => {
  return hasWishlistsRangeValue(filterValue?.[filterKeyMap.WISHLISTS_RANGE]?.value);
};

export const ensureWishlistsMetricSelectionByFilterValue = (
  metricSelectorValues: string[] = [],
  filterValue: any = {},
): string[] => {
  return ensureWishlistsMetricSelection(metricSelectorValues, isWishlistsRangeActive(filterValue));
};

export const ensureWishlistsMetricSelectionByParams = (
  metricSelectorValues: string[] = [],
  params: { metric_filter?: Record<string, any[]> } = {},
): string[] => {
  return ensureWishlistsMetricSelection(metricSelectorValues, hasWishlistsMetricFilter(params?.metric_filter));
};

export const ensureWishlistsSourceMetric = (sourceMetrics: Record<string, string[]> = {}): Record<string, string[]> => {
  const gamalyticMetrics = Array.isArray(sourceMetrics?.gamalytic) ? sourceMetrics.gamalytic : [];

  if (gamalyticMetrics.includes(WISHLISTS_SOURCE_METRIC)) {
    return sourceMetrics;
  }

  return {
    ...sourceMetrics,
    gamalytic: [...gamalyticMetrics, WISHLISTS_SOURCE_METRIC],
  };
};

/**
 * 过滤禁用的指标，返回可用的指标列表、重新生成的 source_metrics 以及校验后的排序字段
 * @param params 包含 date_type, regions, device, order_source, order_metric 等参数
 * @param pageId 页面ID
 * @param cascaderOptions 级联选项
 * @param metricSelectorValuesLet 当前选中的指标值列表（用户勾选的）
 * @param cascaderMap 指标映射表
 * @param getPCConsoleMetricValues 生成 source_metrics 的函数
 */
export const filterDisabledSourceMetrics = (
  params: {
    date_type?: string;
    regions?: string[];
    device?: string[];
    start_date?: string;
    end_date?: string;
    order_source?: string;
    order_metric?: string;
    metric_filter?: Record<string, any[]>;
  },
  pageId: PCConsolePageId,
  cascaderOptions: OptionsItemType[],
  metricSelectorValuesLet: string[],
  cascaderMap?: Record<string, any>,
  getPCConsoleMetricValues?: (metrics: any[]) => {
    order_metric?: string;
    order_source?: string;
    source_metrics?: Record<string, string[]>;
  },
): FilterDisabledMetricsResult => {
  const normalizedMetricValues = ensureWishlistsMetricSelectionByParams(metricSelectorValuesLet, params);
  const preservedMetricValues = getWishlistsPreservedMetricValues(
    hasWishlistsMetricFilter(params?.metric_filter),
  );

  // 根据新的筛选条件计算可用的指标列表
  const { value: availableMetricValues } = getCascaderMetricsOptionsAndValue(
    pageId,
    cascaderOptions,
    normalizedMetricValues,
    {
      date_type: params?.date_type,
      regions: params?.regions,
      device: params?.device,
      start_date: params?.start_date,
      end_date: params?.end_date,
    },
    true,
    preservedMetricValues,
  );

  // 如果提供了 cascaderMap 和 getPCConsoleMetricValues，重新生成 source_metrics
  let sourceMetrics: Record<string, string[]> = {};

  if (cascaderMap && getPCConsoleMetricValues && availableMetricValues?.length) {
    const metricsPCConsole = availableMetricValues?.map((item: any) => cascaderMap?.[item]);
    const metricValues = getPCConsoleMetricValues(metricsPCConsole);
    sourceMetrics = metricValues?.source_metrics || {};
  }

  // 无论 availableMetricValues 是否有值，都校验排序字段
  // 当 sourceMetrics 为空（指标未加载或全部被禁用）时，返回空字符串避免传入无效的 order 字段
  const validatedOrder = validateOrderFields(sourceMetrics, params?.order_source, params?.order_metric);
  const orderSource = validatedOrder.orderSource;
  const orderMetric = validatedOrder.orderMetric;

  return {
    availableMetricValues: availableMetricValues || [],
    sourceMetrics,
    orderSource,
    orderMetric,
  };
};

export interface DerivedMetricSelectorState<
  TColumn extends Record<string, any> = Record<string, any>,
> {
  metrics: Array<TColumn & { disabled: boolean }>;
  metricSelectorOptions: OptionsItemType[];
  metricSelectorValues: string[];
  disabledValue: string[];
}

export interface EnsureValidMetricsParamsArgs<
  TColumn extends Record<string, any> = Record<string, any>,
> {
  params: any;
  shouldUpdateMetrics: boolean;
  filterValueHasChanged: boolean;
  filterOutDisabledValue: boolean;
  metricSelectorValuesLet: string[];
  pageId: PCConsolePageId;
  cascaderOptions: OptionsItemType[];
  cascaderMap: Record<string, TColumn>;
  metricKeys: string[];
  getPCConsoleMetricValues: (metrics: TColumn[]) => {
    order_metric: string;
    order_source: string;
    source_metrics: Record<string, string[]>;
  };
  shouldAppendCombineExtras: boolean;
  defaultMetacritic: Record<string, string[]>;
}

export interface EnsureValidMetricsParamsResult<
  TColumn extends Record<string, any> = Record<string, any>,
> {
  params: any;
  derivedState?: DerivedMetricSelectorState<TColumn>;
  fallbackSortBy?: {
    key?: string;
    name?: string;
  };
}

export interface BuildMetricSelectorChangeArgs<
  TColumn extends Record<string, any> = Record<string, any>,
> {
  payload: string[];
  metricSelectorAvailableValues: string[];
  fetchParams: any;
  isPCConsoleMetricsTable: boolean;
  shouldAppendCombineExtras: boolean;
  defaultPageSize: number;
  pageId: PCConsolePageId;
  cascaderOptions: OptionsItemType[];
  cascaderMap: Record<string, TColumn>;
  getPCConsoleMetricValues: (metrics: TColumn[]) => {
    order_metric: string;
    order_source: string;
    source_metrics: Record<string, string[]>;
  };
  metricValueRangeTime: {
    current?: {
      dateType?: string;
      startDate?: string;
      endDate?: string;
    };
  };
  userManuallyChangedRegionRef: { current: boolean };
  defaultMetacritic: Record<string, string[]>;
}

export interface BuildMetricSelectorChangeResult<
  TColumn extends Record<string, any> = Record<string, any>,
> {
  nextValues: string[];
  metrics: TColumn[];
  metricValues: {
    order_metric: string;
    order_source: string;
    source_metrics: Record<string, string[]>;
  };
  fetchParams: any;
  isValidSortMetric: boolean;
}

export const collectAvailableMetricValues = (options: OptionsItemType[] = []): string[] => {
  const available: string[] = [];
  options.forEach((option) => {
    option?.children?.forEach((child: any) => {
      if (child?.value && !child?.disabled) {
        available.push(child.value);
      }
    });
  });
  return available;
};

export const deriveMetricSelectorState = <
  TColumn extends Record<string, any> = Record<string, any>,
>(
  pageId: PCConsolePageId,
  cascaderOptions: OptionsItemType[],
  cascaderMap: Record<string, TColumn>,
  metricKeys: string[],
  metricSelectorValuesTemp: string[],
  fetchParams: any,
  filterOutDisabledValue: boolean,
): DerivedMetricSelectorState<TColumn> => {
  const normalizedMetricSelectorValues = ensureWishlistsMetricSelectionByParams(
    metricSelectorValuesTemp,
    fetchParams,
  );
  const preservedMetricValues = getWishlistsPreservedMetricValues(
    hasWishlistsMetricFilter(fetchParams?.metric_filter),
  );
  const mergedValues = [
    ...normalizedMetricSelectorValues,
    ...(filterOutDisabledValue ? metricKeys : []),
  ];

  const { options, value, disabledValue } = getCascaderMetricsOptionsAndValue(
    pageId,
    cascaderOptions,
    Array.from(new Set(mergedValues)),
    fetchParams,
    filterOutDisabledValue,
    preservedMetricValues,
  );

  const metrics = value.map((item: string) => {
    const metric = cascaderMap?.[item] || ({} as TColumn);
    return {
      ...metric,
      disabled: disabledValue.includes(item),
    } as TColumn & { disabled: boolean };
  });

  const metricSelectorOptions = options.filter((item: OptionsItemType) => item.value !== 'storeRating');

  return {
    metrics,
    metricSelectorOptions,
    metricSelectorValues: value,
    disabledValue,
  };
};

export const ensureValidMetricsParams = <
  TColumn extends Record<string, any> = Record<string, any>,
>(
  args: EnsureValidMetricsParamsArgs<TColumn>,
): EnsureValidMetricsParamsResult<TColumn> => {
  const {
    params,
    shouldUpdateMetrics,
    filterValueHasChanged,
    filterOutDisabledValue,
    metricSelectorValuesLet,
    pageId,
    cascaderOptions,
    cascaderMap,
    metricKeys,
    getPCConsoleMetricValues,
    shouldAppendCombineExtras,
    defaultMetacritic,
  } = args;

  const nextParams = cloneDeep(params) || {};
  let derivedState: DerivedMetricSelectorState<TColumn> | undefined;
  let fallbackSortBy: EnsureValidMetricsParamsResult<TColumn>['fallbackSortBy'];

  if (shouldUpdateMetrics && filterValueHasChanged) {
    const nextDerivedState = deriveMetricSelectorState(
      pageId,
      cascaderOptions,
      cascaderMap,
      metricKeys,
      metricSelectorValuesLet,
      nextParams,
      filterOutDisabledValue,
    );

    derivedState = nextDerivedState;

    const { metrics } = nextDerivedState;
    const currentMetric = metrics.find((item) => {
      const realDataSource = item?.metricValue || item?.dataSource;
      return (
        realDataSource === nextParams?.order_source &&
        item?.value === nextParams?.order_metric &&
        !item?.disabled
      );
    });

    if (!currentMetric) {
      const availableMetrics = metrics.filter((item) => !item.disabled);
      if (availableMetrics.length) {
        const metricValues = getPCConsoleMetricValues(availableMetrics);
        nextParams.order_metric = metricValues?.order_metric;
        nextParams.order_source = metricValues?.order_source;
        nextParams.source_metrics = {
          ...(metricValues?.source_metrics || {}),
          ...(shouldAppendCombineExtras ? defaultMetacritic : {}),
        };
      } else {
        nextParams.order = 'desc';
        nextParams.order_device = 'PC';
        nextParams.order_metric = 'meta_score';
        nextParams.order_source = 'metacritic';
        nextParams.source_metrics = {
          ...(shouldAppendCombineExtras ? defaultMetacritic : {}),
        };
      }

      const firstAvailableMetric = metrics.find((item) => !item.disabled);
      fallbackSortBy = {
        key: firstAvailableMetric?.value,
        name: firstAvailableMetric?.text,
      };
    }
  }

  return {
    params: nextParams,
    derivedState,
    fallbackSortBy,
  };
};

export const buildMetricSelectorChange = <
  TColumn extends Record<string, any> = Record<string, any>,
>(
  args: BuildMetricSelectorChangeArgs<TColumn>,
): BuildMetricSelectorChangeResult<TColumn> => {
  const {
    payload,
    metricSelectorAvailableValues,
    fetchParams,
    isPCConsoleMetricsTable,
    shouldAppendCombineExtras,
    defaultPageSize,
    pageId,
    cascaderOptions,
    cascaderMap,
    getPCConsoleMetricValues,
    metricValueRangeTime,
    userManuallyChangedRegionRef,
    defaultMetacritic,
  } = args;

  let nextValues = payload;
  if (!nextValues?.length) {
    nextValues = metricSelectorAvailableValues?.slice(0, Math.min(metricSelectorAvailableValues.length, 8));
  }

  // 按 Metrics Category 顺序排序，表格列顺序与筛选器一致
  const sortedValues =
    nextValues?.length && cascaderOptions?.length
      ? getCascaderMetricsOptionsAndValue(
          pageId,
          cascaderOptions,
          nextValues,
          fetchParams,
          false,
        ).value
      : nextValues || [];

  const metrics = (sortedValues || [])
    .map((item) => cascaderMap?.[item])
    .filter((metric): metric is TColumn => Boolean(metric));
  const metricValues = getPCConsoleMetricValues(metrics);
  const isOnlyMscienceMetric = metrics?.every((metricItem) => metricItem?.dataSource === 'mscience');

  const shouldSetUSRegion =
    isPCConsoleMetricsTable &&
    isOnlyMscienceMetric &&
    fetchParams.regions?.[0] === 'global' &&
    !userManuallyChangedRegionRef.current;

  const shouldResetGlobalRegion =
    isPCConsoleMetricsTable &&
    !isOnlyMscienceMetric &&
    fetchParams.regions?.[0] === 'us' &&
    !userManuallyChangedRegionRef.current;

  // 检查传入的排序参数是否在当前选中的指标列表中
  const isValidSortMetric = metrics.some((item) => {
    const realDataSource = item?.metricValue || item?.dataSource;
    return (
      realDataSource === fetchParams?.order_source &&
      item?.value === fetchParams?.order_metric
    );
  });

  const baseParams = {
    ...fetchParams,
    ...(shouldSetUSRegion ? { regions: ['us'] } : {}),
    ...(shouldResetGlobalRegion ? { regions: ['global'] } : {}),
    // 只有当传入的排序参数无效时，才用 metricValues 覆盖
    order_metric: isValidSortMetric ? fetchParams?.order_metric : metricValues?.order_metric,
    order_source: isValidSortMetric ? fetchParams?.order_source : metricValues?.order_source,
    source_metrics: {
      ...(metricValues?.source_metrics || {}),
      ...(shouldAppendCombineExtras ? defaultMetacritic : {}),
    },
    page: 1,
    page_size: defaultPageSize,
    date_type: metricValueRangeTime?.current?.dateType,
    start_date: metricValueRangeTime?.current?.startDate,
    end_date: metricValueRangeTime?.current?.endDate,
  };

  return {
    nextValues: sortedValues,
    metrics,
    metricValues,
    fetchParams: baseParams,
    isValidSortMetric, // 新增返回值，供 applyMetricSelectionSideEffects 使用
  };
};
